import { DateTime } from 'luxon';
import Decimal from 'decimal.js';
import { Entity, ItemLinkEntity } from '../entity';
import { Job } from '../job/job';
import { ActivityEntityName } from '../shared/activity.entity.name';
import { EmbeddedCode } from '../shared/code';
import { TaxRate } from '../taxrate/tax.rate';
import { ContactEntity } from '../contact/contact.options';
import { Company } from '../tenant/company';
import { LinkRelationship } from '../../enums/link.relationship';
import { InvoiceUtility } from '../../utilities/invoice.utility';
import { PurchaseOrderLine, TaxInclusiveOption } from './purchase.order.line';
import { Work } from '../work/work';
import { PurchaseOrder, PurchaseOrderBillingCompany, PurchaseOrderStatus } from './purchase.order';
import * as Enumerable from 'linq';

function buildPurchaseOrderLineFromEmbeddedCode(taxRate: TaxRate, code: EmbeddedCode, itemLink?: ItemLinkEntity): PurchaseOrderLine {

    const price = new Decimal((code.buyPrice ?? 0) * (code.codeUoM?.conversionRate ?? 1)).toDP(4).toNumber();

    const line: PurchaseOrderLine = {
        _id: code._id,
        name: code.name,
        description: code.description,
        quantity: code.quantity,
        customPrice: true,
        price,
        subTotal: 0,
        total: 0,
        tax: 0,
        location: code.location,
        notes: code.note,
        codeStatus: code.status,
        uom: code.uom,
        codeUoM: code.codeUoM,
        glCode: null,
        taxRate: taxRate,
        type: code.type,
        createFromCode: true,
        canOverrideDesc: !!code.canOverrideDesc,
        itemLink,
    };

    return line;
}

export function buildPurchaseOrder(company: Company, user: ContactEntity): PurchaseOrder {

    let billingCompany: PurchaseOrderBillingCompany;

    if (company) {
        billingCompany = {
            _id: company._id,
            name: company.name,
            address: company.address,
            phone: company.phone,
            email: company.email,
            taxRate: company.taxRate,
            taxId: company.taxId
        };
    }

    return {
        _id: null,
        poNumber: null,
        poDate: DateTime.now().setZone(company?.address?.timezone ?? 'utc').toJSDate(),
        billingCompany: billingCompany,
        reference: null,
        supplier: null,
        supplierMarkup: 0,
        comment: null,
        status: PurchaseOrderStatus.Draft,
        lines: [],
        createdBy: user,
        chargeable: true,
        taxRate: null,
        deliveryOptions: 'pickup',
        deliveryAddress: null,
        taxInclusiveOption: TaxInclusiveOption.Exclusive,
        subTotal: 0,
        total: 0,
        tax: 0,
        links: [],
        theme: null
    };
}

export function buildPurchaseOrderFromJob(job: Job, company: Company, taxRate: TaxRate, user: ContactEntity, codesIds: string[] = []): PurchaseOrder {

    const template = buildPurchaseOrder(company, user);

    let lines: PurchaseOrderLine[] = [];

    if (codesIds?.length) {
        lines = Enumerable
            .from(job.workOrders)
            .selectMany(wo => {


                const codes = Enumerable
                    .from(wo.codes)
                    .where(c => codesIds?.length
                        ? codesIds.includes(c._id)
                        : true
                    )
                    .select(c => {
                        const itemLink: ItemLinkEntity = {
                            _id: wo._id,
                            name: wo.workOrderNumber,
                            lineId: c._id,
                        };

                        return buildPurchaseOrderLineFromEmbeddedCode(taxRate, c, itemLink)
                    })
                    .toArray();
                return codes;
            })
            .toArray();
    }

    // I did feel shame for using 'any' here. But too scared to change the InvoiceUtility
    const formattedLines = InvoiceUtility.formatLinesPrice(lines as any);
    const { subTotal, tax, total } = InvoiceUtility.getInvoiceLineTotal(formattedLines, taxRate, template.taxInclusiveOption);

    return <PurchaseOrder>{
        ...template,
        job: {
            _id: job._id,
            name: job.jobNumber
        },
        poDate: DateTime.now().setZone(job?.site?.address?.timezone).toJSDate(),
        lines: lines,
        subTotal,
        tax,
        total,
        kind: lines.length > 0
            ? ActivityEntityName.Item
            : ActivityEntityName.Job,
        links: [
            {
                _id: job._id,
                type: ActivityEntityName.Job,
                name: job.jobNumber,
                relationship: LinkRelationship.From
            }
        ]
    }
}

export function buildPurchaseOrderFromWork(work: Work, company: Company, taxRate: TaxRate, user: ContactEntity, codesIds: string[] = []): PurchaseOrder {

    const template = buildPurchaseOrder(company, user);

    let lines: PurchaseOrderLine[] = [];

    if (codesIds?.length) {
        // TODO: implement logic
    }

    // I did feel shame for using 'any' here. But too scared to change the InvoiceUtility
    const formattedLines = InvoiceUtility.formatLinesPrice(lines as any);
    const { subTotal, tax, total } = InvoiceUtility.getInvoiceLineTotal(formattedLines, taxRate, template.taxInclusiveOption);

    return <PurchaseOrder>{
        ...template,
        poDate: DateTime.now().setZone(work?.site?.address?.timezone).toJSDate(),
        lines: lines,
        subTotal,
        tax,
        total,
        kind: ActivityEntityName.Work,
        links: [
            {
                _id: work._id,
                type: ActivityEntityName.Work,
                name: work.workNumber,
                relationship: LinkRelationship.From
            }
        ]
    }
}

export function isPurchaseOrderKindItem(po: PurchaseOrder) {
    return po?.kind === ActivityEntityName.Item || Enumerable.from(po?.lines).any(x => !!x.itemLink?._id);
}
